import { API_URL } from "./config";
import React, { useEffect, useState } from 'react';
import token from './Token';
import './DayPreview.css';
import moment from 'moment';
import { FaCalendarAlt, FaCheckSquare, FaRunning, FaListUl, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { getSubsubValidityStatus, parseSubsubDate } from './subsubValidity';

const SUBSUB_TIME_UNIT_SCALE = 2;

const DayPreview = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bucket, setBucket] = useState('today'); // 'today', 'thisWeek', 'nextWeek', 'thisMonth', 'expired'
    const [currentTime, setCurrentTime] = useState(moment());

    useEffect(() => {
        // Update time every minute
        const timer = setInterval(() => setCurrentTime(moment()), 60000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${API_URL}/daypreview?token=${token}&includeDaily=true`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const result = await response.json();
                console.log(result);
                setData(result);
            } catch (error) {
                console.error("Failed to fetch day preview:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return <div className="loading-container">Loading Day Preview...</div>;
    }

    if (!data) {
        return <div className="loading-container">Failed to load data.</div>;
    }

    const bucketLabels = {
        today: 'Today',
        thisWeek: 'This Week',
        nextWeek: 'Next Week',
        thisMonth: 'This Month',
        expired: 'Expired'
    };

    // --- Helper Functions for Expired/Valid Badges ---

    // Format duration for badge (e.g., "1D", "2H", "3W")
    const formatDurationBadge = (duration) => {
        const days = Math.abs(duration.asDays());
        const hours = Math.abs(duration.asHours());
        const weeks = Math.abs(duration.asWeeks());
        const months = Math.abs(duration.asMonths());

        if (months >= 1) {
            return `${Math.floor(months)}M`;
        } else if (weeks >= 1) {
            return `${Math.floor(weeks)}W`;
        } else if (days >= 1) {
            return `${Math.floor(days)}D`;
        } else {
            return `${Math.floor(hours)}H`;
        }
    };

    // Format date for tooltip (DD/MM HH:MM)
    const formatTooltipDate = (date) => {
        const parsed = parseSubsubDate(date);
        return parsed ? parsed.format('DD/MM HH:mm') : '';
    };

    // Check if a subtask is expired
    const isSubtaskExpired = (sub) => {
        if (!sub.expiryDate) return false;
        return moment(sub.expiryDate).isBefore(currentTime);
    };

    // Get expired duration for a subtask
    const getExpiredDuration = (expiryDate) => {
        const expiry = moment(expiryDate);
        return moment.duration(currentTime.diff(expiry));
    };

    // Check if a subsubtask is expired
    const isSubsubExpired = (subsub) => {
        const validUntil = parseSubsubDate(subsub.expiryDate || subsub.validUntil);
        if (!validUntil) return false;
        return validUntil.isBefore(currentTime);
    };

    // Check if a subsubtask is not yet valid (future)
    const isSubsubFuture = (subsub) => {
        const validFrom = parseSubsubDate(subsub.validFrom);
        if (!validFrom) return false;
        return validFrom.isAfter(currentTime);
    };

    // Get duration until subsubtask becomes valid
    const getValidInDuration = (validFrom) => {
        const from = parseSubsubDate(validFrom);
        if (!from) return moment.duration(0);
        return moment.duration(from.diff(currentTime));
    };

    // Render the expired badge for subtasks
    const renderExpiredBadge = (expiryDate) => {
        const duration = getExpiredDuration(expiryDate);
        const badgeText = formatDurationBadge(duration);
        const tooltipText = formatTooltipDate(expiryDate);

        return (
            <span className="badge badge-expired" title={`Expired: ${tooltipText}`}>
                {badgeText}
            </span>
        );
    };

    // Render the valid-in badge for future subsubtasks
    const renderValidInBadge = (validFrom) => {
        const duration = getValidInDuration(validFrom);
        const badgeText = formatDurationBadge(duration);
        const tooltipText = formatTooltipDate(validFrom);

        return (
            <span className="badge badge-future" title={`Active from: ${tooltipText}`}>
                {badgeText}
            </span>
        );
    };

    // Render the subsub expired badge
    const renderSubsubExpiredBadge = (validUntil) => {
        const parsedValidUntil = parseSubsubDate(validUntil);
        const duration = parsedValidUntil
            ? moment.duration(currentTime.diff(parsedValidUntil))
            : moment.duration(0);
        const badgeText = formatDurationBadge(duration);
        const tooltipText = formatTooltipDate(validUntil);

        return (
            <span className="badge badge-expired" title={`Expired: ${tooltipText}`}>
                {badgeText}
            </span>
        );
    };

    // Filter subsubtasks based on view mode
    const filterSubsubs = (subsubs) => {
        if (!subsubs || subsubs.length === 0) return [];

        return subsubs.map(ss => {
            const expired = isSubsubExpired(ss);
            const future = isSubsubFuture(ss);
            const active = !expired && !future;

            return {
                ...ss,
                _expired: expired,
                _future: future,
                _active: active
            };
        });
    };

    const getSubsubAllocatedTime = (value) => {
        if (value === "" || value === null || value === undefined) return null;
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) return null;
        return numeric * SUBSUB_TIME_UNIT_SCALE;
    };

    // --- Data Rendering Helpers ---

    // Helper to get inclusive buckets
    const getBucketedItems = (buckets, currentBucket) => {
        let items = [];
        if (currentBucket === 'today') {
            items = [...(buckets.today || [])];
        } else if (currentBucket === 'thisWeek') {
            items = [...(buckets.today || []), ...(buckets.thisWeek || [])];
        } else if (currentBucket === 'nextWeek') {
            items = [
                ...(buckets.today || []),
                ...(buckets.thisWeek || []),
                ...(buckets.nextWeek || [])
            ];
        } else if (currentBucket === 'thisMonth') {
            items = [
                ...(buckets.today || []),
                ...(buckets.thisWeek || []),
                ...(buckets.nextWeek || []),
                ...(buckets.thisMonth || [])
            ];
        }
        return items;
    };

    const renderTasks = () => {
        const tasks = data.tasks.byTask || [];

        // Collect all subtasks based on the current bucket
        const allSubtasks = [];

        tasks.forEach(task => {
            if (!task.subs) return;

            const taskSubs = [];

            if (bucket === 'expired') {
                // Expired mode: only show expired subtasks
                if (task.subs.expired && task.subs.expired.length > 0) {
                    taskSubs.push(...task.subs.expired.map(sub => ({ ...sub, _isExpired: true })));
                }
            } else {
                // All other modes: always include expired subtasks first
                if (task.subs.expired && task.subs.expired.length > 0) {
                    taskSubs.push(...task.subs.expired.map(sub => ({ ...sub, _isExpired: true })));
                }

                // Always include active tasks
                if (task.subs.active && task.subs.active.length > 0) {
                    taskSubs.push(...task.subs.active.map(sub => ({ ...sub, _isExpired: false })));
                }

                // Include expiring tasks based on bucket (inclusive)
                if (bucket === 'thisWeek' || bucket === 'nextWeek' || bucket === 'thisMonth') {
                    if (task.subs.expireThisWeek && task.subs.expireThisWeek.length > 0) {
                        taskSubs.push(...task.subs.expireThisWeek.map(sub => ({ ...sub, _isExpired: false })));
                    }
                }

                if (bucket === 'nextWeek' || bucket === 'thisMonth') {
                    if (task.subs.expireNextWeek && task.subs.expireNextWeek.length > 0) {
                        taskSubs.push(...task.subs.expireNextWeek.map(sub => ({ ...sub, _isExpired: false })));
                    }
                }

                if (bucket === 'thisMonth') {
                    if (task.subs.expireThisMonth && task.subs.expireThisMonth.length > 0) {
                        taskSubs.push(...task.subs.expireThisMonth.map(sub => ({ ...sub, _isExpired: false })));
                    }
                }
            }

            if (taskSubs.length > 0) {
                allSubtasks.push({
                    task: task,
                    subs: taskSubs
                });
            }
        });

        if (allSubtasks.length === 0) return null;

        return (
            <div className="section-card">
                <div className="section-title">
                    {bucket === 'expired' ? <FaExclamationTriangle /> : <FaListUl />} Tasks ({bucketLabels[bucket]})
                </div>
                {allSubtasks.map((item, tIdx) => (
                    <div key={tIdx} className="task-group">
                        <div className="task-header">{item.task.tname}</div>
                        {item.subs.map((sub, idx) => {
                            const processedSubsubs = filterSubsubs(sub.subsubs);

                            return (
                                <div key={idx} className={`preview-item item-task ${sub._isExpired ? 'item-expired' : ''}`}>
                                    <div className="item-header">
                                        <div className="item-name-container">
                                            <span className="item-name">{sub.sname}</span>
                                            {sub._isExpired && sub.expiryDate && renderExpiredBadge(sub.expiryDate)}
                                        </div>
                                        <div className="item-tags">
                                            {sub.time > 0 && <span className="tag">{sub.time}h</span>}
                                        </div>
                                    </div>
                                    {/* Subtask expiry date display */}
                                    {sub.expiryDate && (() => {
                                        const expiry = moment(sub.expiryDate);
                                        const duration = sub._isExpired
                                            ? moment.duration(currentTime.diff(expiry))
                                            : moment.duration(expiry.diff(currentTime));
                                        const relativeTime = formatDurationBadge(duration);
                                        const tooltipDate = formatTooltipDate(sub.expiryDate);

                                        return (
                                            <div className="item-date-info">
                                                <span className={`date-label ${sub._isExpired ? 'date-expired' : 'date-due'}`}>
                                                    {sub._isExpired ? '⚠ Expired' : '📅 Due'}
                                                </span>
                                                <span className="date-value date-relative" title={tooltipDate}>
                                                    {sub._isExpired ? `${relativeTime} ago` : `in ${relativeTime}`}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    <div className="item-meta">
                                        {sub.tags && sub.tags.split(',').map(t => <span key={t} className="tag">{t}</span>)}
                                    </div>
                                    {processedSubsubs.length > 0 && (
                                        <ul className="sub-list">
                                            {processedSubsubs.map((ss, ssIdx) => {
                                                const subsubTime = getSubsubAllocatedTime(ss.time);
                                                const validityStatus = getSubsubValidityStatus(ss, currentTime);
                                                const validUntilValue = ss.expiryDate || ss.validUntil;

                                                return (
                                                    <li key={ssIdx} className={`sub-item ${ss._expired ? 'subsub-expired' : ''} ${ss._future ? 'subsub-future' : ''}`}>
                                                        <div className="subsub-content">
                                                            <div className="subsub-header">
                                                                <span className="subsub-name">{ss.name}</span>
                                                                <span className="subsub-badges">
                                                                    {subsubTime !== null && <span className="tag">{subsubTime}h</span>}
                                                                    {ss._expired && validUntilValue && renderSubsubExpiredBadge(validUntilValue)}
                                                                    {ss._future && ss.validFrom && renderValidInBadge(ss.validFrom)}
                                                                </span>
                                                            </div>
                                                            {validityStatus.label && (
                                                                <div
                                                                    className="subsub-validity-summary"
                                                                    style={{ color: validityStatus.labelColor }}
                                                                    title={validUntilValue ? `Valid until: ${formatTooltipDate(validUntilValue)}` : undefined}
                                                                >
                                                                    {validityStatus.label}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    const renderTodos = () => {
        if (bucket === 'expired') return null; // Todos don't have expired state

        // Todos have buckets: active, thisWeek, nextWeek, thisMonth
        const todoBuckets = {
            today: data.todos.active,
            thisWeek: data.todos.thisWeek,
            nextWeek: data.todos.nextWeek,
            thisMonth: data.todos.thisMonth
        };

        const items = getBucketedItems(todoBuckets, bucket);

        if (items.length === 0) return null;

        return (
            <div className="section-card">
                <div className="section-title"><FaCheckSquare /> Todos ({bucketLabels[bucket]})</div>
                {items.map((todo, idx) => (
                    <div key={idx} className="preview-item item-todo">
                        <div className="item-header">
                            <div className="item-name">{todo.content}</div>
                        </div>
                        <div className="item-meta">
                            {todo.deferUntil && <span>Due: {moment(todo.deferUntil).format('MMM D, HH:mm')}</span>}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderHabits = () => {
        if (bucket === 'expired') return null; // Habits don't have expired state in this context

        const items = getBucketedItems(data.habits.buckets, bucket);
        if (items.length === 0) return null;

        return (
            <div className="section-card">
                <div className="section-title"><FaRunning /> Habits ({bucketLabels[bucket]})</div>
                {items.map((habit, idx) => (
                    <div key={idx} className="preview-item item-habit">
                        <div className="item-header">
                            <div className="item-name">{habit.name}</div>
                            <span className="tag">{habit.count}/{habit.totalCount}</span>
                        </div>
                        <div className="item-meta">
                            {habit.dueAt && <span>Due: {moment(habit.dueAt).format('HH:mm')}</span>}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderProgrammed = () => {
        if (bucket === 'expired') return null; // Programmed doesn't have expired state

        const stamps = data.programmed.byStamp || [];
        let items = [];

        const aggBuckets = { today: [], thisWeek: [], nextWeek: [], thisMonth: [] };

        stamps.forEach(stamp => {
            if (stamp.buckets) {
                if (stamp.buckets.today) aggBuckets.today.push(...stamp.buckets.today);
                if (stamp.buckets.thisWeek) aggBuckets.thisWeek.push(...stamp.buckets.thisWeek);
                if (stamp.buckets.nextWeek) aggBuckets.nextWeek.push(...stamp.buckets.nextWeek);
                if (stamp.buckets.thisMonth) aggBuckets.thisMonth.push(...stamp.buckets.thisMonth);
            }
        });

        items = getBucketedItems(aggBuckets, bucket);

        if (items.length === 0) return null;

        return (
            <div className="section-card">
                <div className="section-title"><FaClock /> Programmed ({bucketLabels[bucket]})</div>
                {items.map((prog, idx) => (
                    <div key={idx} className="preview-item item-programmed">
                        <div className="item-header">
                            <div className="item-name">{prog.sname}</div>
                        </div>
                        <div className="item-meta">
                            <span>Scheduled: {moment(prog.nextScheduled).format('MMM D, HH:mm')}</span>
                            <span className="tag">{prog.frequencyType}</span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderCalendar = () => {
        if (bucket === 'expired') return null; // Calendar doesn't have expired state

        const items = getBucketedItems(data.calendar.buckets, bucket);
        if (items.length === 0) return null;

        return (
            <div className="section-card">
                <div className="section-title"><FaCalendarAlt /> Calendar ({bucketLabels[bucket]})</div>
                {items.map((evt, idx) => (
                    <div key={idx} className="preview-item item-calendar">
                        <div className="item-header">
                            <div className="item-name">{evt.summary}</div>
                        </div>
                        <div className="item-meta">
                            <span>{moment(evt.start).format('MMM D, HH:mm')} - {moment(evt.end).format('HH:mm')}</span>
                            {evt.location && <span>@ {evt.location}</span>}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const tasksContent = renderTasks();
    const programmedContent = renderProgrammed();
    const habitsContent = renderHabits();
    const calendarContent = renderCalendar();
    const todosContent = renderTodos();

    return (
        <div className="day-preview-container">
            <header className="preview-header">
                <h1 className="preview-title">Day Preview</h1>
                <div className="bucket-controls">
                    {Object.keys(bucketLabels).map(key => (
                        <button
                            key={key}
                            className={`bucket-btn ${bucket === key ? 'active' : ''} ${key === 'expired' ? 'bucket-btn-expired' : ''}`}
                            onClick={() => setBucket(key)}
                        >
                            {bucketLabels[key]}
                        </button>
                    ))}
                </div>
            </header>

            <div className="dashboard-grid">
                {/* Column 1: Main Focus (Tasks) */}
                {tasksContent && (
                    <div className="dashboard-col">
                        {tasksContent}
                    </div>
                )}

                {/* Column 2: Routine (Programmed & Habits) */}
                {(programmedContent || habitsContent) && (
                    <div className="dashboard-col">
                        {programmedContent}
                        {habitsContent}
                    </div>
                )}

                {/* Column 3: Calendar & Todos */}
                {(calendarContent || todosContent) && (
                    <div className="dashboard-col">
                        {calendarContent}
                        {todosContent}
                    </div>
                )}
            </div>

        </div>
    );
};

export default DayPreview;
